import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { logger } from './logger';

/**
 * Business Review Modal PDF export.
 *
 * Captures the modal as a single tall image via html2canvas and emits a
 * one-page PDF whose page dimensions match the captured aspect ratio.
 * The result is "the dashboard, but on a PDF" — no pagination, no print
 * dialog, deterministic output.
 */

// 1 CSS px → 0.75pt (96dpi web ↔ 72dpi print). Sizing the PDF page to match
// the capture's natural CSS-pixel width means text renders at its source pt
// size instead of being squeezed from a ~1200px modal into a 595pt A4 page
// (which made every glyph ~3pt and unreadable).
const CSS_PX_TO_PT = 72 / 96;

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// ─── Capture-time CSS ───────────────────────────────────────────────────────
// html2canvas does not honor `@media print`, so any layout normalization the
// printed view used to rely on has to live under a class selector instead.

const CAPTURE_STYLE_ID = 'localytics-pdf-capture-style';
const CAPTURE_CLASS = 'localytics-pdf-capturing';

const CAPTURE_CSS = `
[data-pdf-root].${CAPTURE_CLASS} [data-pdf-body] {
  zoom: 1 !important;
}

[data-pdf-root].${CAPTURE_CLASS} .recharts-responsive-container,
[data-pdf-root].${CAPTURE_CLASS} .recharts-wrapper {
  width: 100% !important;
}

[data-pdf-root].${CAPTURE_CLASS} svg {
  overflow: visible !important;
}

[data-pdf-root].${CAPTURE_CLASS} * {
  -webkit-line-clamp: unset !important;
  -webkit-box-orient: initial !important;
}

/* Block-level text that can safely become display: block (no flex parent
   relies on its inline behavior). */
[data-pdf-root].${CAPTURE_CLASS} .chakra-heading,
[data-pdf-root].${CAPTURE_CLASS} .chakra-text,
[data-pdf-root].${CAPTURE_CLASS} [style*="-webkit-line-clamp"],
[data-pdf-root].${CAPTURE_CLASS} [style*="--chakra-line-clamp"] {
  display: block !important;
  overflow: visible !important;
  text-overflow: clip !important;
  white-space: normal !important;
  max-width: none !important;
}

[data-pdf-root].${CAPTURE_CLASS} .chakra-modal__close-btn,
[data-pdf-root].${CAPTURE_CLASS} [aria-label="Export as PDF"],
[data-pdf-root].${CAPTURE_CLASS} [aria-label="Add to favorites"],
[data-pdf-root].${CAPTURE_CLASS} [aria-label="Remove from favorites"] {
  display: none !important;
}
`;

const injectCaptureStyles = () => {
  if (document.getElementById(CAPTURE_STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = CAPTURE_STYLE_ID;
  style.textContent = CAPTURE_CSS;
  document.head.appendChild(style);
};

const removeCaptureStyles = () => {
  const existing = document.getElementById(CAPTURE_STYLE_ID);
  if (existing) existing.remove();
};

const waitForImages = (root) => {
  const imgs = Array.from(root.querySelectorAll('img'));
  return Promise.all(
    imgs.map((img) => {
      if (img.complete && img.naturalWidth > 0) return Promise.resolve();
      return new Promise((resolve) => {
        const done = () => resolve();
        img.addEventListener('load', done, { once: true });
        img.addEventListener('error', done, { once: true });
        setTimeout(done, 2500);
      });
    })
  );
};

// ─── Capture ────────────────────────────────────────────────────────────────

const captureFullModal = async (pdfRootElement) => {
  const scrollBody = pdfRootElement.querySelector('[data-pdf-body]');
  const modalContent =
    pdfRootElement.closest('.chakra-modal__content') || pdfRootElement.parentElement;
  const captureWidth = pdfRootElement.offsetWidth || 1200;

  const saved = {
    bodyOverflow: scrollBody?.style.overflow || '',
    bodyMaxHeight: scrollBody?.style.maxHeight || '',
    bodyHeight: scrollBody?.style.height || '',
    bodyFlex: scrollBody?.style.flex || '',
    rootHeight: pdfRootElement.style.height,
    rootMaxHeight: pdfRootElement.style.maxHeight,
    rootOverflow: pdfRootElement.style.overflow,
    rootWidth: pdfRootElement.style.width,
    rootMinWidth: pdfRootElement.style.minWidth,
    modalMaxHeight: modalContent?.style.maxHeight || '',
    modalHeight: modalContent?.style.height || '',
    modalOverflow: modalContent?.style.overflow || '',
    modalWidth: modalContent?.style.width || '',
    modalMinWidth: modalContent?.style.minWidth || '',
    modalMaxWidth: modalContent?.style.maxWidth || '',
  };

  injectCaptureStyles();
  pdfRootElement.classList.add(CAPTURE_CLASS);

  try {
    pdfRootElement.style.width = captureWidth + 'px';
    pdfRootElement.style.minWidth = captureWidth + 'px';
    if (modalContent) {
      modalContent.style.width = captureWidth + 'px';
      modalContent.style.minWidth = captureWidth + 'px';
      modalContent.style.maxWidth = captureWidth + 'px';
      modalContent.style.maxHeight = 'none';
      modalContent.style.height = 'auto';
      modalContent.style.overflow = 'visible';
    }
    if (scrollBody) {
      scrollBody.style.overflow = 'visible';
      scrollBody.style.maxHeight = 'none';
      scrollBody.style.height = 'auto';
      scrollBody.style.flex = '0 0 auto';
    }
    pdfRootElement.style.height = 'auto';
    pdfRootElement.style.maxHeight = 'none';
    pdfRootElement.style.overflow = 'visible';

    try {
      if (document.fonts && document.fonts.ready) await document.fonts.ready;
    } catch (_) {
      /* ignore */
    }
    await waitForImages(pdfRootElement);
    await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
    await sleep(150);

    // Pin height + windowHeight to the fully-expanded scrollHeight so html2canvas
    // doesn't truncate content past the viewport when cloning the element.
    const fullHeight = pdfRootElement.scrollHeight;

    const canvas = await html2canvas(pdfRootElement, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#FFFFFF',
      logging: false,
      removeContainer: true,
      foreignObjectRendering: false,
      scrollX: 0,
      scrollY: 0,
      width: captureWidth,
      height: fullHeight,
      windowWidth: captureWidth,
      windowHeight: fullHeight,
      // Replace every Chakra Tag in the clone with a clean inline-block
      // <span> that carries the original bg/color/border-radius/font-color
      // but none of the Chakra classes. Class-based + inline-style overrides
      // weren't winning against Chakra's emotion CSS for these elements;
      // wholesale replacement guarantees a clean render.
      //
      // Chakra v2.8 doesn't put a `.chakra-tag` class on <Tag> — it's a bare
      // <chakra.span> with only an emotion-generated hash class. But every
      // <Tag> in this modal lives inside a <Wrap>, so the WrapItem (which
      // *does* get a class) is a reliable anchor: the Tag is its first-child
      // <span>.
      onclone: (clonedDoc, clonedRoot) => {
        const root = clonedRoot || clonedDoc;
        const win = clonedDoc.defaultView || window;
        const tags = Array.from(
          root.querySelectorAll('.chakra-wrap__listitem > span')
        );
        tags.forEach((tag) => {
          const cs = win.getComputedStyle(tag);
          const replacement = clonedDoc.createElement('span');
          replacement.textContent = tag.textContent || '';
          replacement.style.cssText =
            'display:inline-block;' +
            'vertical-align:middle;' +
            'overflow:visible;' +
            'box-sizing:border-box;' +
            'line-height:1.5;' +
            'padding:5px 10px;' +
            'border-radius:9999px;' +
            'white-space:nowrap;' +
            'font-family:' + cs.fontFamily + ';' +
            'font-size:' + cs.fontSize + ';' +
            'font-weight:' + cs.fontWeight + ';' +
            'background-color:' + cs.backgroundColor + ';' +
            'color:' + cs.color + ';' +
            'border:' + cs.borderTopWidth + ' ' + cs.borderTopStyle + ' ' + cs.borderTopColor + ';';
          tag.parentNode && tag.parentNode.replaceChild(replacement, tag);
        });
        // Style Badges in place — they were already rendering correctly,
        // just need extra padding + line-height for descender room.
        root.querySelectorAll('.chakra-badge').forEach((el) => {
          el.style.cssText +=
            ';display:inline-block!important;' +
            'vertical-align:middle!important;' +
            'overflow:visible!important;' +
            'height:auto!important;' +
            'min-height:0!important;' +
            'line-height:1.5!important;' +
            'padding-top:4px!important;' +
            'padding-bottom:4px!important;' +
            'white-space:normal!important;';
        });
      },
    });
    canvas.__captureWidthPx = captureWidth;
    return canvas;
  } finally {
    pdfRootElement.classList.remove(CAPTURE_CLASS);
    removeCaptureStyles();
    if (scrollBody) {
      scrollBody.style.overflow = saved.bodyOverflow;
      scrollBody.style.maxHeight = saved.bodyMaxHeight;
      scrollBody.style.height = saved.bodyHeight;
      scrollBody.style.flex = saved.bodyFlex;
    }
    pdfRootElement.style.height = saved.rootHeight;
    pdfRootElement.style.maxHeight = saved.rootMaxHeight;
    pdfRootElement.style.overflow = saved.rootOverflow;
    pdfRootElement.style.width = saved.rootWidth;
    pdfRootElement.style.minWidth = saved.rootMinWidth;
    if (modalContent) {
      modalContent.style.maxHeight = saved.modalMaxHeight;
      modalContent.style.height = saved.modalHeight;
      modalContent.style.overflow = saved.modalOverflow;
      modalContent.style.width = saved.modalWidth;
      modalContent.style.minWidth = saved.modalMinWidth;
      modalContent.style.maxWidth = saved.modalMaxWidth;
    }
  }
};

// ─── Canvas → single tall PDF page ──────────────────────────────────────────

const canvasToPdf = (canvas, filename, captureWidthPx) => {
  // Page width in pt = source CSS px * (72/96). Height keeps the captured
  // aspect ratio. The canvas is at scale=2, but captureWidthPx is the
  // original CSS-pixel width, which is what we want for sizing.
  const widthPt = captureWidthPx * CSS_PX_TO_PT;
  const heightPt = (canvas.height / canvas.width) * widthPt;
  const pdf = new jsPDF({
    orientation: heightPt >= widthPt ? 'portrait' : 'landscape',
    unit: 'pt',
    format: [widthPt, heightPt],
    compress: true,
  });
  const imgData = canvas.toDataURL('image/jpeg', 0.92);
  pdf.addImage(imgData, 'JPEG', 0, 0, widthPt, heightPt);
  pdf.save(filename);
};

// ─── Public API ─────────────────────────────────────────────────────────────

/**
 * Primary entry point used by BusinessReviewModal.
 * Always produces a single-page PDF whose page is sized to the captured
 * modal's aspect ratio.
 */
export const generateScreenshotPdf = async (pdfRootElement, businessName) => {
  if (!pdfRootElement) return;
  try {
    const canvas = await captureFullModal(pdfRootElement);
    const safeName = (businessName || 'business').replace(/[^a-z0-9]/gi, '_');
    canvasToPdf(canvas, `${safeName}_report.pdf`, canvas.__captureWidthPx);
  } catch (err) {
    logger.error('[PDF_EXPORT] Failed to generate PDF', err);
    throw err;
  }
};

// Legacy named exports kept for backwards compatibility with older imports.
export const captureChartImages = async () => ({});
export const generateBusinessReportPdf = () => {};
